import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { getAllCaseStudies } from '../../data/caseStudyData';
import { useUserState } from '../../lib/userState';

/**
 * "Walk a finished build" - photo-led horizontal scroller of finished
 * builds. Case studies are all SA today, so for non-SA visitors we
 * strip the suburb / "Adelaide" language and treat the reel as generic
 * inspiration ("Real homes we have helped families build" without a
 * state claim). We keep the same photos and story links - the copy is
 * the only thing that changes.
 */
const HomeFinishedBuildsReel = () => {
  const userState = useUserState();
  const isSa = userState === 'SA';

  // Pull all case studies that have a card image, fall back to hero image.
  // The tile order is shuffled once per mount so the reel does not always
  // lead with the same few stories.
  const builds = useMemo(() => {
    const mapped = getAllCaseStudies()
      .filter((s) => s.cardImage || s.heroImage)
      .map((s) => ({
        src: s.cardImage || s.heroImage,
        // On SA we badge the tile with the suburb. Off SA we surface the
        // project type instead so nothing implies the build is local to
        // the visitor's picked state.
        caption: isSa ? (s.client?.suburb || s.title || s.name || 'Adelaide') : (s.project?.type || s.title || 'Build story'),
        to: `/case-studies/${s.slug}`,
        tag: s.project?.type || 'Build Story',
        slug: s.slug,
      }));
    // Fisher-Yates shuffle.
    for (let i = mapped.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [mapped[i], mapped[j]] = [mapped[j], mapped[i]];
    }
    return mapped;
  }, [isSa]);

  if (builds.length === 0) return null;

  return (
    <section className="bg-[#fafbfc] py-10 sm:py-20" data-testid="finished-builds-reel" data-reel-state={userState}>
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row items-center sm:items-end sm:justify-between flex-wrap gap-4">
          <div className="text-center sm:text-left">
            <h2 className="text-[22px] sm:text-[32px] font-bold text-[#1d1d1f] tracking-[-0.02em] leading-[1.15] max-w-[680px] mx-auto sm:mx-0">
              {isSa ? (
                <>
                  <span className="gradient-text-blue">Real Adelaide homes</span>, brought to life.
                </>
              ) : (
                <>
                  <span className="gradient-text-blue">Real homes</span>, brought to life.
                </>
              )}
            </h2>
          </div>
          <Link
            to="/case-studies"
            className="text-[13.5px] font-medium text-[#1d1d1f] hover:text-[#1d8efe] transition-colors inline-flex items-center gap-1"
          >
            Browse all stories <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* Horizontal scroller, full-bleed feel on mobile */}
      <div className="overflow-x-auto scrollbar-none">
        <div className="inline-flex gap-3 sm:gap-4 px-4 sm:px-6 lg:px-8 pb-4 min-w-full">
          {builds.map((b) => (
            <Link
              key={b.slug}
              to={b.to}
              className="group relative shrink-0 w-[230px] sm:w-[290px] aspect-square rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.18)] transition-all"
              data-testid={`finished-build-${b.slug}`}
            >
              <img
                src={b.src}
                alt={isSa ? `Finished build in ${b.caption}, Adelaide` : `Finished ${b.tag.toLowerCase()} - BuildPilot case study`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 text-white">
                <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-white/75 mb-1 truncate">
                  {b.tag}
                </p>
                <p className="text-[16px] sm:text-[18px] font-bold leading-tight mb-0.5 truncate">{b.caption}</p>
                <span className="inline-flex items-center gap-1 text-[12.5px] text-white/85 group-hover:text-white">
                  See the story <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeFinishedBuildsReel;
